import { Box } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import InputComponent from 'UiKit/input';
import SubmitFormButton from 'UiKit/button/submitFormButton';
import { Condition } from 'Component/others';
import { EyeCloseIcon, EyeOpenIcon } from 'Asset/icon';
import { formResetPassword } from 'Const/reset-password';
import useResetPassword from 'Domain/reset-password/usePostResetPassword';

import ChecklistItem from 'View/reset-password/component/checklist-item';
import { useResetPasswordState } from 'View/reset-password/state';

import styles from './styles.module.css';

const Form = () => {
  const { register, handleSubmit, formState, watch } = useForm();

  const { token } = useParams();
  const { mutate: onResetPassword } = useResetPassword({ token });
  const onSubmit = (data) => onResetPassword(data);

  const { state, updateState } = useResetPasswordState();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const hasUpperCase = /[A-Z]/.test(password);
  const hasSpecialCharacter = /[!@#$%^&*]/.test(password);
  const isMinCharacter = password?.length >= 8;

  const allowSubmit = password && confirmPassword && isMinCharacter && hasUpperCase && hasSpecialCharacter;

  return (
    <Box className={styles['form__container']} id="reset-password-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputComponent
          label={formResetPassword.password.label}
          placeholder={formResetPassword.password.placeholder}
          margin="0 0 24px 0"
          error={formState?.errors?.password}
          value={password}
          type={state?.isOpenEyePassword ? 'text' : 'password'}
          isRequired={true}
          id="password"
          name="password"
          rightButton={
            <Box
              className={styles['eye__icon']}
              onClick={() => updateState({ isOpenEyePassword: !state?.isOpenEyePassword })}
              role="button"
              aria-label="Toggle password visibility"
            >
              <Condition statement={state?.isOpenEyePassword} fallback={<EyeCloseIcon size="18" />}>
                <EyeOpenIcon size="18" />
              </Condition>
            </Box>
          }
          register={{
            ...register('password', {
              required: formResetPassword.error.passwordRequired,
            }),
          }}
        />

        <InputComponent
          label={formResetPassword.confirmPassword.label}
          placeholder={formResetPassword.confirmPassword.placeholder}
          margin="0 0 24px 0"
          error={formState?.errors?.confirmPassword}
          value={confirmPassword}
          type={state?.isOpenEyeConfirmPassword ? 'text' : 'password'}
          isRequired={true}
          id="confirmPassword"
          name="confirmPassword"
          rightButton={
            <Box
              className={styles['eye__icon']}
              onClick={() => updateState({ isOpenEyeConfirmPassword: !state?.isOpenEyeConfirmPassword })}
              role="button"
              aria-label="Toggle confirm password visibility"
            >
              <Condition statement={state?.isOpenEyeConfirmPassword} fallback={<EyeCloseIcon />}>
                <EyeOpenIcon size="18" />
              </Condition>
            </Box>
          }
          register={{
            ...register('confirmPassword', {
              required: formResetPassword.error.passwordRequired,
              validate: (value) => {
                if (value !== password) {
                  return formResetPassword.error.passwordNotMatch;
                }
                return true;
              },
            }),
          }}
        />

        <Box className={styles['checklist__container']}>
          <ChecklistItem text={formResetPassword.rules.minCharacter(8)} isActive={isMinCharacter} />
          <ChecklistItem text={formResetPassword.rules.hasUpperCase} isActive={hasUpperCase} />
          <ChecklistItem text={formResetPassword.rules.hasSpecialCharacter} isActive={hasSpecialCharacter} />
        </Box>

        <SubmitFormButton text={formResetPassword.button.send} height="42px" width="100%" isLoading={onResetPassword?.isPending} isValid={allowSubmit} />
      </form>
    </Box>
  );
};

export default Form;
